import React from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { change } from "redux-form";
import { z } from "zod";

import { fetchWithAuth } from "~/common/fetch";
import { initializeBaseInstruction } from "~/common/instructions";
import { type LlmPrescription, PrescriptionGenerateStatusSchema } from "~/common/prescription_llm_schema";
import { API_PATIENT_PRESCRIPTION_GENERATE, API_PATIENT_PRESCRIPTION_GENERATE_STATUS } from "~/config";
import { addInstructions, selectInstructions } from "~/slices/instructions";
import { selectCorrectionFormValues } from "~/slices/redux-form";
import { selectSmartPrescription, smartPrescriptionFailed, smartPrescriptionFilled, smartPrescriptionProcessingStarted, smartPrescriptionReset } from "~/slices/smart_prescription";
import type { TPatientAudioNote } from "~/types/patient";
import type { TPrescription } from "~/types/prescription";

import { isSmartPrescriptionFillingSelected } from "../patient_new/patient_new_additional_services";

const StartJobResponseSchema = z.object({ job_id: z.string(), status: z.literal("processing") });

type PollJobResponse = z.infer<typeof PrescriptionGenerateStatusSchema>;

// 40 × 1.5s = 60s: the backend's verifier loop can retry the model up to 4 times.
const GENERATE_POLL_ATTEMPTS = 40;
const GENERATE_POLL_INTERVAL_MS = 1500;
const PROCESSING_ANALYSIS_STEP_DELAY_MS = 6000;

async function pollGenerateJob(jobId: string, isCancelled: () => boolean): Promise<Extract<PollJobResponse, { status: "ready" | "failed" }> | null> {
  for (let attempt = 0; attempt < GENERATE_POLL_ATTEMPTS; attempt += 1) {
    if (attempt > 0) {
      await new Promise((resolve) => window.setTimeout(resolve, GENERATE_POLL_INTERVAL_MS));
    }
    if (isCancelled()) {
      return null;
    }
    const pollResponse = await fetchWithAuth(API_PATIENT_PRESCRIPTION_GENERATE_STATUS(jobId));
    if (isCancelled()) {
      return null;
    }
    if (!pollResponse.ok) {
      continue;
    }
    const pollParsed = PrescriptionGenerateStatusSchema.safeParse(await pollResponse.json());
    if (!pollParsed.success) {
      continue;
    }
    if (pollParsed.data.status !== "processing") {
      return pollParsed.data;
    }
  }
  return null;
}

/** Coerce so the redux-form converter never sees null collections. */
function sanitize(prescription: LlmPrescription): LlmPrescription {
  return {
    ...prescription,
    close_all_gaps_spaces: prescription.close_all_gaps_spaces ?? {},
    elastics_options: prescription.elastics_options ?? {},
    dont_move: prescription.dont_move ?? [],
    avoid_locks: prescription.avoid_locks ?? [],
    extractions: prescription.extractions ?? [],
    teething: prescription.teething ?? [],
    occlusal_plane_teeth: prescription.occlusal_plane_teeth ?? [],
  };
}

function isEmptyGeneratedValue(value: unknown): boolean {
  return value == null || value === "" || (Array.isArray(value) && value.length === 0) || (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0);
}

function hasAudioNoteContent(audioNote: Partial<TPatientAudioNote> | null | undefined): boolean {
  return Boolean(
    audioNote &&
    ((audioNote.uploads?.length ?? 0) > 0 ||
      (audioNote.files?.length ?? 0) > 0 ||
      audioNote.transcript_text ||
      audioNote.transcript_status === "processing" ||
      audioNote.transcript_status === "ready" ||
      audioNote.id != null),
  );
}

export type SmartPrescriptionCoordinatorControllerProps = {
  /** Edit mode: the transcript already on mount must NOT re-fill the saved recipe —
   * only a later, new transcript supplements it. */
  deferInitialFill?: boolean;
  enabledOverride?: boolean;
};

export function useSmartPrescriptionCoordinator({ deferInitialFill = false, enabledOverride }: SmartPrescriptionCoordinatorControllerProps = {}) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const instructions = useSelector(selectInstructions);
  const formValues = useSelector(selectCorrectionFormValues);
  const formValuesRef = React.useRef(formValues);
  formValuesRef.current = formValues;
  const { operation, isRecording, isUploading, uploadError, micError, isProcessing, error } = useSelector(selectSmartPrescription);

  const audioNote = instructions.audio_note;
  const audioNoteRef = React.useRef(audioNote);
  audioNoteRef.current = audioNote;
  const uploadsCount = audioNote?.uploads?.length ?? 0;
  const hasAudioContent = hasAudioNoteContent(audioNote);
  const baselineUploadsRef = React.useRef(deferInitialFill ? uploadsCount : 0);
  const baselineInitializedRef = React.useRef(!deferInitialFill || hasAudioContent);
  if (deferInitialFill && !baselineInitializedRef.current && hasAudioContent) {
    baselineUploadsRef.current = uploadsCount;
    baselineInitializedRef.current = true;
  }
  const isRecipeUpdate = operation === "recipe-update";
  const hasNewAudio = isRecipeUpdate || !deferInitialFill || uploadsCount > baselineUploadsRef.current;
  const transcribing = audioNote?.transcript_status === "processing" && hasNewAudio;

  const previousTranscriptStatusRef = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    const status = audioNote?.transcript_status;
    if (hasNewAudio && previousTranscriptStatusRef.current === "processing" && status === "empty" && error == null && uploadError == null) {
      dispatch(smartPrescriptionFailed("В записи не удалось распознать речь. Проверьте микрофон и перезапишите сообщение."));
    }
    previousTranscriptStatusRef.current = status;
  }, [audioNote?.transcript_status, dispatch, error, hasNewAudio, uploadError]);

  const [dismissed, setDismissed] = React.useState(false);
  React.useEffect(() => {
    if (isUploading || isProcessing) {
      setDismissed(false);
    }
  }, [isUploading, isProcessing]);

  // Visual step for the processing view (0 распознаём · 1 заполняем · 2 проверяем) —
  // the model call is one opaque request, so 1→2 uses a deliberate presentation delay.
  const [procStep, setProcStep] = React.useState(0);
  React.useEffect(() => {
    if (isUploading || transcribing) {
      setProcStep(0);
      return;
    }
    if (isProcessing) {
      setProcStep(1);
      const timer = window.setTimeout(() => setProcStep(2), PROCESSING_ANALYSIS_STEP_DELAY_MS);
      return () => window.clearTimeout(timer);
    }
  }, [isUploading, transcribing, isProcessing]);

  const [hasResetOnMount, setHasResetOnMount] = React.useState(false);
  React.useLayoutEffect(() => {
    dispatch(smartPrescriptionReset());
    setHasResetOnMount(true);
    return () => {
      dispatch(smartPrescriptionReset());
    };
  }, [dispatch]);

  const enabled = enabledOverride ?? isSmartPrescriptionFillingSelected(instructions.additional_service_tags);
  const transcript = audioNote?.transcript_status === "ready" && audioNote.transcript_text ? audioNote.transcript_text : null;
  // Re-transcription of the SAME audio changes the text but not the upload count —
  // that must never re-run the model over an already-saved recipe.
  // State triggers the render needed to clear pendingProcessing, even if the effect dispatches nothing.
  const [processedTranscript, setProcessedTranscript] = React.useState<string | null>(deferInitialFill ? transcript : null);

  const [pendingJobId, setPendingJobId] = React.useState<string | null>(null);

  const applyGenerateResult = React.useCallback(
    (result: Extract<PollJobResponse, { status: "ready" | "failed" }> | null, jobId: string) => {
      if (result === null) {
        setPendingJobId(jobId);
        dispatch(smartPrescriptionFailed(intl.formatMessage({ id: "additional.services.smart.generate_slow" })));
        return;
      }
      setPendingJobId(null);
      if (result.status === "failed" || !result.prescription) {
        dispatch(smartPrescriptionFailed(result.error || "Модель не вернула рецепт"));
        return;
      }
      const values = initializeBaseInstruction(sanitize(result.prescription) as TPrescription);
      Object.entries(values).forEach(([field, value]) => {
        if (deferInitialFill && isEmptyGeneratedValue(value) && !isEmptyGeneratedValue((formValuesRef.current as Record<string, unknown> | undefined)?.[field])) {
          return;
        }
        dispatch(change("correction", field, value as never));
      });
      dispatch(smartPrescriptionFilled({ meta: result.meta }));
      if (result.meta) {
        dispatch(addInstructions({ audio_note: { ...audioNoteRef.current, prescription_meta: result.meta } }));
      }
    },
    [deferInitialFill, dispatch, intl],
  );

  const resumeGenerateWaiting = React.useCallback(async () => {
    if (!pendingJobId) {
      return;
    }
    dispatch(smartPrescriptionProcessingStarted(transcript ?? ""));
    const result = await pollGenerateJob(pendingJobId, () => false);
    applyGenerateResult(result, pendingJobId);
  }, [pendingJobId, transcript, dispatch, applyGenerateResult]);

  React.useEffect(() => {
    // A transcript received during recording is processed after recording stops.
    if (!enabled || !transcript || isRecording || processedTranscript === transcript) {
      return;
    }
    setProcessedTranscript(transcript);

    if (deferInitialFill && !isRecipeUpdate) {
      if (uploadsCount <= baselineUploadsRef.current) {
        return;
      }
      baselineUploadsRef.current = uploadsCount;
    }

    let cancelled = false;
    dispatch(smartPrescriptionProcessingStarted(transcript));

    void (async () => {
      try {
        const startResponse = await fetchWithAuth(API_PATIENT_PRESCRIPTION_GENERATE, {
          method: "POST",
          body: JSON.stringify({ transcript }),
        });
        if (!startResponse.ok) {
          throw new Error("Не удалось обработать транскрипт");
        }
        const startParsed = StartJobResponseSchema.safeParse(await startResponse.json());
        if (!startParsed.success) {
          throw new Error("Не удалось обработать транскрипт");
        }
        const { job_id: jobId } = startParsed.data;
        if (cancelled) {
          return;
        }

        const result = await pollGenerateJob(jobId, () => cancelled);
        if (cancelled) {
          return;
        }
        applyGenerateResult(result, jobId);
      } catch (err) {
        if (!cancelled) {
          dispatch(smartPrescriptionFailed(err instanceof Error ? err.message : "Ошибка обработки"));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // `processedTranscript` deliberately NOT a dep: listing it would re-fire this effect
    // right as it's set, cancelling the fetch this very invocation just started.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, transcript, isRecording, uploadsCount, dispatch, deferInitialFill, isRecipeUpdate]);

  // Keep the modal open until generation starts after transcription completes.
  const pendingProcessing = hasNewAudio && transcript != null && processedTranscript !== transcript;
  const hardBusy = hasResetOnMount && (isUploading || isProcessing || pendingProcessing);
  const processing = hardBusy || (hasResetOnMount && transcribing);
  const errorText = hasResetOnMount ? (micError ?? uploadError ?? error) : null;
  const showModal = hasResetOnMount && !isRecording && (hardBusy || ((transcribing || errorText != null) && !dismissed));

  const handleDismiss = React.useCallback(() => setDismissed(true), []);
  const runAction = React.useCallback(async (action?: () => void | Promise<void>) => {
    try {
      await action?.();
    } finally {
      setDismissed(true);
    }
  }, []);

  return {
    enabled,
    hardBusy,
    processing,
    operation,
    procStep,
    transcribing,
    uploadError,
    micError,
    errorText,
    showModal,
    handleDismiss,
    runAction,
    resumeGenerateWaiting: pendingJobId ? resumeGenerateWaiting : undefined,
  };
}

export type SmartPrescriptionCoordinatorController = ReturnType<typeof useSmartPrescriptionCoordinator>;
